import React, { ReactElement, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import Icon from 'components/Icon';
import { Row } from 'components/layout';
import { ButtonV3 } from 'components/ComponentV2';
import { ActionMenuButton } from 'components/ActionMenuButton/ActionMenuButton.component';
import { useNewsletters } from 'hooks/useNewsletters.hook';
import { useNotification } from 'hooks/useNotification';
import { PAGE_ROUTES } from 'lib/page-routes';
import { TouchpointType } from 'lib/models/touchpoint';
import { useSocietyGlobal } from 'lib/contexts/society-global';
import { GlobalSearchProvider } from 'lib/providers/GlobalSearchProvider';
import { SearchPageType } from 'lib/utils/global.search';
import { Featured } from '../Featured/Featured.component';
import { OpportunityList } from '../OpportunityList/OpportunityList.component';

import styles from './CareerOpportunity.module.scss';

export const CareerOpportunities = (): ReactElement => {
  const router = useRouter();
  const notificationInstance = useNotification();
  const { selectedTouchpoints, getCopiedLink } = useSocietyGlobal();
  const { createNewsletter } = useNewsletters();
  const [t] = useTranslation('career-opportunities');
  const { noOfJobs, noOfInternships } = useMemo(() => {
    let jobs = 0;
    let internships = 0;

    selectedTouchpoints.forEach(({ touchpointable_type }) => {
      if (touchpointable_type === TouchpointType.Job) {
        jobs++;
      } else if (touchpointable_type === TouchpointType.Internship) {
        internships++;
      }
    });

    return { noOfJobs: jobs, noOfInternships: internships };
  }, [selectedTouchpoints]);

  const ShareOptions = useMemo(
    () => [
      {
        id: '1',
        label: t('share-jobs', {
          noOfJobs: noOfJobs,
        }),
        onClick: () => handleShareListClick(TouchpointType.Job),
      },
      {
        id: '2',
        label: t('share-internships', {
          noOfInternships: noOfInternships,
        }),
        onClick: () => handleShareListClick(TouchpointType.Internship),
      },
    ],
    [selectedTouchpoints]
  );

  const letterContent = JSON.stringify({
    time: new Date().getTime(),
    blocks: [
      {
        type: 'paragraph',
        data: {
          text: t('news-letter-text'),
          level: 1,
        },
      },
      {
        type: 'paragraph',
        data: {
          text: t('news-letter-text-2'),
          level: 1,
        },
      },
    ],
  });

  const onSendList = async () => {
    try {
      const data = await createNewsletter({
        news_letter: {
          subject: t('news-letter-heading'),
          letter: letterContent,
          touchpoint_ids: selectedTouchpoints.map(
            (selectedTouchpoint) => selectedTouchpoint.id
          ),
        },
      });

      if (data) {
        router.push(`${PAGE_ROUTES.NEWSLETTER_EDIT}?id=${data.id}`, undefined, {
          shallow: true,
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleShareListClick = async (type: TouchpointType | 'All' = 'All') => {
    try {
      const linkToCopy = await getCopiedLink(type);
      await navigator.clipboard.writeText(linkToCopy || '');
      notificationInstance.acknowledge({
        message: 'Link copied',
        timeout: 10000,
        showClose: true,
        icon: 'icon_tick-mark',
      });
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <>
      <section
        className={styles['career-opportunities']}
        data-testid="CareerOpportunities"
      >
        <div className={styles.header}>
          <Row
            align="center"
            columnGap={4}
            className={styles.redirect}
            onClick={() => router.push(PAGE_ROUTES.DASHBOARD)}
          >
            <Icon iconName="chevron-left" size="large" />
            {t('dashboard')}
          </Row>
          <div className={styles['header-content']}>
            <p className={styles.title}>{t('career-opportunities')}</p>
            <div className={styles['header-button-container']}>
              <ButtonV3
                className={styles['header-button']}
                size="medium"
                onClick={onSendList}
                startIcon="icon_mail"
                iconSize="medium"
                startIconClassName={styles['header-button-icon']}
                disabled={selectedTouchpoints.length === 0}
              >
                {t('send-list')}
              </ButtonV3>
              {noOfJobs > 0 && noOfInternships > 0 ? (
                <ActionMenuButton
                  options={ShareOptions}
                  buttonText={t('share-list')}
                  buttonStartIcon="icon_link"
                  buttonColor="secondary"
                  buttonSize="medium"
                  buttonClassName={styles['action-button']}
                />
              ) : (
                <ButtonV3
                  className={styles['header-button']}
                  size="medium"
                  disabled={selectedTouchpoints.length === 0}
                  onClick={() => {
                    handleShareListClick(
                      selectedTouchpoints.every(
                        (tp) => tp.touchpointable_type === TouchpointType.Job
                      )
                        ? TouchpointType.Job
                        : TouchpointType.Internship
                    );
                  }}
                  color="secondary"
                  startIcon="icon_link"
                  iconSize="medium"
                  startIconClassName={styles['header-button-icon']}
                >
                  {t('share-list')}
                </ButtonV3>
              )}
            </div>
          </div>
        </div>
        <div className={styles['body-container']}>
          <Featured />
          <GlobalSearchProvider
            requestedPageType={SearchPageType.JOBS_AND_INTERNSHIPS}
            initialQueryParams={{ type: SearchPageType.JOBS_AND_INTERNSHIPS }}
          >
            {/* Render recommended opportunities */}
            <OpportunityList
              withRecommendation={true}
              title={t('recommended-title')}
              className="recommended-opportunities"
              style={{ marginBottom: '20px' }}
            />
          </GlobalSearchProvider>

          <GlobalSearchProvider
            requestedPageType={SearchPageType.JOBS_AND_INTERNSHIPS}
            initialQueryParams={{ type: SearchPageType.JOBS_AND_INTERNSHIPS }}
          >
            {/* Render other opportunities */}
            <OpportunityList
              withRecommendation={false}
              title={t('other-opportunity-title')}
              className="other-opportunities"
              style={{ marginBottom: '20px' }}
            />
          </GlobalSearchProvider>
        </div>
      </section>
    </>
  );
};

CareerOpportunities.displayName = 'CareerOpportunities';
